import { axiosApi } from '@/services/http/axiosApi'
import {
  API_GET_EMPLOYEE,
  API_GET_EMPLOYEES_BY_COMPANY_ID,
  API_GET_INVITATIONS_BY_COMPANY_ID,
  API_PATCH_EMPLOYEE,
  API_POST_INVITATIONS_BY_COMPANY_ID,
  API_POST_TERMS_AND_CONDITIONS_ACCEPT
} from '@/constants/urls'
import type { COLLABORATOR_STATUS } from '@/constants/collaborator'
import type { AxiosResponse } from 'axios'
import type { DeepPartial } from '@/helpers/utils'

export enum EMPLOYEE_PERMISSIONS {
  MANAGE_EMPLOYEES = 'MANAGE_EMPLOYEES',
  BURN_POINTS = 'BURN_POINTS',
  VIEW_ANALYTICS = 'VIEW_ANALYTICS',
  AAB_COMPANY_PORTAL_READ = 'AAB_COMPANY_PORTAL_READ'
}

export interface EmployeeType {
  pmid: string
  companyId: string
  professionalEmail: string
  personalInfo: {
    firstName: string
    lastName: string
    jobTitle?: string
    jobRole?: string
  }
  authorization: {
    role: COLLABORATOR_STATUS
    permissions?: EMPLOYEE_PERMISSIONS[]
  }
  invitation?: {
    invitationDate?: string
    invitedBy?: {
      firstname: string
      lastname: string
    }
  }
  compliance: {
    termsAndConditions: {
      isAccepted: boolean
      acceptedDate?: string
    }
  }
  status: string
  tracking?: {
    createdDate: string
    createdBy: string
    updatedDate: string
    updatedBy: string
  }
  company?: {
    id: string
    name: string
  }
}

export interface EmployeesByCompanyIdResponse {
  employees: EmployeeType[]
  page: number
  pageSize: number
  total: number
  totalPages: number
}

export interface InvitationInvitee {
  role: COLLABORATOR_STATUS
  email: string
  lastName?: string
  firstName?: string
}

export interface InvitationInvitedBy {
  lastName: string
  firstName: string
}

export interface InvitationType {
  id: string
  invitee: InvitationInvitee
  invitedBy: InvitationInvitedBy
  sendDate: string
}

export interface InvitationsByCompanyIdResponse {
  invitations: InvitationType[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

export interface InvitationPostBody {
  invitees: InvitationInvitee[]
}

export interface InvitationPostSuccessItem {
  id: string
  invitee: InvitationInvitee
  status: string
}

export interface InvitationPostErrorItem {
  email: string
  code: string
}

export interface InvitationPostResponse {
  success?: InvitationPostSuccessItem[]
  errors?: InvitationPostErrorItem[]
}

export const getEmployeesByCompanyId = async (
  companyId: string,
  pageSize: number = 3000
): Promise<AxiosResponse<EmployeesByCompanyIdResponse>> =>
  await axiosApi.get(`${API_GET_EMPLOYEES_BY_COMPANY_ID(companyId)}`, {
    params: {
      pageSize
    }
  })

export const getEmployee = async (): Promise<AxiosResponse<EmployeeType>> =>
  await axiosApi.get(API_GET_EMPLOYEE)

export const patchEmployee = async (
  companyId: string,
  employeeId: string,
  employee: DeepPartial<EmployeeType>
): Promise<AxiosResponse<EmployeeType>> =>
  await axiosApi.patch(API_PATCH_EMPLOYEE(companyId, employeeId), employee)

export const acceptTermsAndConditions = async (): Promise<AxiosResponse<void>> =>
  await axiosApi.post(API_POST_TERMS_AND_CONDITIONS_ACCEPT)

export const getInvitationsByCompanyId = async (
  companyId: string,
  pageSize: number = 3000
): Promise<AxiosResponse<InvitationsByCompanyIdResponse>> =>
  await axiosApi.get(`${API_GET_INVITATIONS_BY_COMPANY_ID(companyId)}`, {
    params: {
      pageSize
    }
  })

export const postInvitations = async (
  companyId: string,
  body: InvitationPostBody
): Promise<AxiosResponse<InvitationPostResponse>> =>
  await axiosApi.post(API_POST_INVITATIONS_BY_COMPANY_ID(companyId), body)
